import { Children, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from 'v5/design-system/DropdownMenu';
import { Button } from 'v5/design-system/Button';
import { Kbd } from 'v5/design-system/Kbd';
import { ChevronDown, X } from 'lucide-react';

export interface BatchActionsLayoutProps
  extends React.ComponentPropsWithRef<'div'> {
  selectedCount: number;
  entityName: {
    singular: string;
    plural: string;
  };
  children: React.ReactNode;
  alternateContent?: React.ReactNode;
  dropdownAlign?: 'start' | 'end';
  onClearSelection?: () => void;
}

export const BatchActionsLayout = forwardRef<
  HTMLDivElement,
  BatchActionsLayoutProps
>(
  (
    {
      selectedCount,
      entityName,
      children,
      alternateContent,
      dropdownAlign = 'end',
      onClearSelection,
      className,
      ...rest
    },
    ref
  ) => {
    const entityLabel =
      selectedCount === 1 ? entityName.singular : entityName.plural;
    const displayText = `${selectedCount} ${entityLabel} selected`;
    const hasSelection = selectedCount > 0;
    const { t } = useTranslation('patterns', {
      keyPrefix: 'BatchActionsLayout'
    });

    return (
      <div ref={ref} className={className} {...rest}>
        <AnimatePresence mode="wait" initial={false}>
          {hasSelection ? (
            <motion.div
              key="batch-actions"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.1 }}
              className="flex w-fit items-center gap-3 bg-bg-emphasis text-bg-default p-2 pl-3 rounded-lg -my-2"
            >
              <div className="flex items-center min-w-0">{displayText}</div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="secondary">
                    Select action
                    <ChevronDown className="ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align={dropdownAlign}>
                  {children}
                  {onClearSelection && (
                    <>
                      {/* only between actions and clearing: a host may have no action for this selection */}
                      {Children.toArray(children).length > 0 && (
                        <DropdownMenuSeparator />
                      )}
                      <DropdownMenuItem onSelect={onClearSelection}>
                        <X />
                        {t('clearSelection')}
                        {/* the hosts' selection clears on Escape too */}
                        <Kbd className="ml-auto normal-case">Esc</Kbd>
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </motion.div>
          ) : alternateContent ? (
            <motion.div
              key="alternate-content"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 4 }}
              transition={{ duration: 0.1 }}
            >
              {alternateContent}
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    );
  }
);

BatchActionsLayout.displayName = 'BatchActionsLayout';
